import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Badge,
  Spinner,
  Alert,
  Modal,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import HeadingBanner from "../components/HeadingBanner";
import LeftPanel from "../components/LeftPanel";
import OrientationPrompt from "../components/OrientationPrompt";
import ManageRulesModal from "../components/ManageRulesModal";
import ManageTemplatesModal from "../components/ManageTemplatesModal";
import JobHistoryModal from "../components/JobHistoryModal";
import EventLogModal from "../components/EventLogModal";
import TestSchoolMessageModal from "../components/TestSchoolMessageModal";
import {
  FaBell,
  FaEnvelope,
  FaPlay,
  FaExclamationTriangle,
  FaClock,
  FaChartLine,
  FaFlask,
} from "react-icons/fa";
import {
  whoami,
  getJobRunStatistics,
  getJobRunHealth,
  getLatestJobRun,
  getSchoolMetricEventStatistics,
  getCommunicationRules,
  getTemplatesCounts,
  triggerJobRun,
} from "../services/APIClient";
import "../components/SchoolAdmin.css";

// Constants
const CONSTANTS = {
  SCHEDULED_RUN_TIME: "00:00",
  RULES_FETCH_LIMIT: 100,
  TOAST_DURATION: 5000,
  HEALTH_STATUS: {
    HEALTHY: "healthy",
  },
  COLORS: {
    SUCCESS: "#10b981",
    WARNING: "#f59e0b",
    PRIMARY: "#3b82f6",
    SECONDARY: "#8b5cf6",
    INFO: "#06b6d4",
    TEST: "#ec4899",
  },
};

// Dashboard sections configuration (cleaned up and consolidated)
const getDashboardSections = (stats, handleOpenModal) => [
  {
    category: "Configuration",
    items: [
      {
        name: "Communication Rules",
        icon: FaBell,
        color: CONSTANTS.COLORS.PRIMARY,
        description: "Create and edit notification rules",
        value: Array.isArray(stats.rules)
          ? `${stats.rules.filter((r) => r.active).length} active`
          : "0 active",
        subtext: `${Array.isArray(stats.rules) ? stats.rules.length : 0} total`,
        onClick: () => handleOpenModal("manageRules"),
      },
      {
        name: "Message Templates",
        icon: FaEnvelope,
        color: CONSTANTS.COLORS.SECONDARY,
        description: "Customize email and in-platform messages",
        value: stats.templateCounts
          ? `${stats.templateCounts.email + stats.templateCounts.inPlatform} templates`
          : "0 templates",
        subtext: stats.templateCounts
          ? `${stats.templateCounts.email} email, ${stats.templateCounts.inPlatform} in-app`
          : "No templates",
        onClick: () => handleOpenModal("manageTemplates"),
      },
    ],
  },
  {
    category: "Monitoring & History",
    items: [
      {
        name: "Job Execution History",
        icon: FaClock,
        color: CONSTANTS.COLORS.INFO,
        description: "Threshold check runs and system health",
        value: stats.health?.hoursSinceLastRun !== undefined
          ? `${stats.health.hoursSinceLastRun}h ago`
          : "No runs yet",
        subtext: stats.health
          ? `Status: ${stats.health.status.toUpperCase()}`
          : "Checking...",
        badge: stats.health?.status !== CONSTANTS.HEALTH_STATUS.HEALTHY
          ? { text: "Check Status", color: "warning" }
          : null,
        onClick: () => handleOpenModal("viewJobHistory"),
      },
      {
        name: "Event & Notification Log",
        icon: FaChartLine,
        color: CONSTANTS.COLORS.WARNING,
        description: "Triggered events and sent notifications",
        value: stats.events?.totalEvents || "0",
        subtext: stats.jobRuns?.totalNotificationsSent
          ? `${stats.jobRuns.totalNotificationsSent} notifications sent`
          : "No notifications yet",
        badge:
          stats.events?.eventsWithErrors > 0
            ? {
                text: `${stats.events.eventsWithErrors} errors`,
                color: "danger",
              }
            : null,
        onClick: () => handleOpenModal("viewEvents"),
      },
    ],
  },
];

// Stat card component (updated to show subtext)
const StatCard = ({ item }) => (
  <Card
    className="class-admin-card"
    onClick={item.onClick}
    style={{ cursor: "pointer" }}
  >
    <Card.Body className="class-admin-card-body">
      <div
        className="class-admin-icon"
        style={{ backgroundColor: `${item.color}15`, color: item.color }}
      >
        <item.icon size={20} />
      </div>
      <div className="class-admin-content">
        <h6 className="class-admin-title">{item.name}</h6>
        <p className="class-admin-description">{item.description}</p>
        {item.value && (
          <div
            style={{
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: item.color,
              marginBottom: "0.25rem",
            }}
          >
            {item.value}
          </div>
        )}
        {item.subtext && (
          <div
            className="text-muted"
            style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}
          >
            {item.subtext}
          </div>
        )}
        {item.badge && (
          <Badge bg={item.badge.color} style={{ marginTop: "0.5rem" }}>
            {item.badge.text}
          </Badge>
        )}
      </div>
    </Card.Body>
  </Card>
);

StatCard.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    color: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subtext: PropTypes.string,
    badge: PropTypes.shape({
      text: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
    }),
    onClick: PropTypes.func.isRequired,
  }).isRequired,
};

function SchoolMessagingDash() {
  // User state
  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState("Loading...");
  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  // Dashboard state
  const [stats, setStats] = useState({
    jobRuns: null,
    health: null,
    latestRun: null,
    events: null,
    rules: [],
    templateCounts: null,
  });
  const [loading, setLoading] = useState(true);
  const [triggeringJob, setTriggeringJob] = useState(false);
  const [error, setError] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState({
    manageRules: false,
    manageTemplates: false,
    viewJobHistory: false,
    viewEvents: false,
    confirmTrigger: false,
    testSchool: false,
  });

  // Toast state
  const [toasts, setToasts] = useState([]);

  // Add toast notification
  const addToast = (message, variant = "info") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, variant }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, CONSTANTS.TOAST_DURATION);
  };

  // Handle orientation changes
  useEffect(() => {
    const handleOrientationChange = (e) => setIsPortrait(e.matches);
    const mediaQuery = window.matchMedia("(orientation: portrait)");
    mediaQuery.addEventListener("change", handleOrientationChange);
    return () =>
      mediaQuery.removeEventListener("change", handleOrientationChange);
  }, []);

  // Initialize dashboard
  useEffect(() => {
    const initDashboard = async () => {
      try {
        const response = await whoami();
        setTeacherid(response.data.stakeholder.id);
        setName(response.data.stakeholder.name);
      } catch (error) {
        console.error("Failed to load user data:", error);
        setError("Failed to load user information. Please refresh the page.");
        addToast("Failed to load user information", "danger");
      }
      await loadDashboardData();
    };

    initDashboard();
  }, []);

  // Load all dashboard data
  const loadDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [jobStats, health, latestRun, eventStats, rules, templateCounts] =
        await Promise.all([
          getJobRunStatistics().catch((e) => {
            console.error("Job stats error:", e);
            return null;
          }),
          getJobRunHealth().catch((e) => {
            console.error("Health check error:", e);
            return null;
          }),
          getLatestJobRun().catch((e) => {
            console.error("Latest run error:", e);
            return null;
          }),
          getSchoolMetricEventStatistics().catch((e) => {
            console.error("Event stats error:", e);
            return null;
          }),
          getCommunicationRules({
            filter: { limit: CONSTANTS.RULES_FETCH_LIMIT },
          }).catch((e) => {
            console.error("Rules error:", e);
            return [];
          }),
          getTemplatesCounts().catch((e) => {
            console.error("Template counts error:", e);
            return null;
          }),
        ]);

      setStats({
        jobRuns: jobStats,
        health: health,
        latestRun: latestRun,
        events: eventStats,
        rules: Array.isArray(rules) ? rules : [],
        templateCounts: templateCounts,
      });
    } catch (error) {
      console.error("Error loading dashboard data:", error);
      setError(
        "Some dashboard data failed to load. Please try refreshing the page."
      );
      addToast("Error loading dashboard data", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Handle manual job trigger
  const handleTriggerJob = async () => {
    setTriggeringJob(true);
    try {
      const result = await triggerJobRun();
      addToast(
        `Job triggered successfully! Schools: ${result.schoolsProcessed}, Events: ${result.eventsCreated}, Notifications: ${result.notificationsSent}`,
        "success"
      );
      await loadDashboardData();
    } catch (error) {
      console.error("Job trigger error:", error);
      addToast(
        `Failed to trigger job: ${error.message || "Unknown error"}`,
        "danger"
      );
    } finally {
      setTriggeringJob(false);
      handleCloseModal("confirmTrigger");
    }
  };

  // Handle test completion
  const handleTestComplete = (result) => {
    const totalNotifications = result.results?.reduce(
      (sum, r) => sum + (r.notificationsSent || 0),
      0
    );
    addToast(
      `Test completed for ${result.schoolName}. ${totalNotifications} notifications sent.`,
      "success"
    );
    loadDashboardData();
  };

  // Modal handlers
  const handleOpenModal = (modalName) => {
    setShowModal((prev) => ({ ...prev, [modalName]: true }));
  };

  const handleCloseModal = (modalName) => {
    setShowModal((prev) => ({ ...prev, [modalName]: false }));
  };

  return (
    <div>
      <Container fluid className="class-admin-container">
        <HeadingBanner
          name={`Welcome ${name}!`}
          isLoggedIn={true}
          cloud={true}
          teacherid={teacherid}
        />

        <Row className="class-admin-row">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="class-admin-content">
            {/* Page Header */}
            <div className="class-admin-header">
              <h2 className="class-admin-page-title">
                Messaging & Notifications Dashboard
              </h2>
              <p className="class-admin-page-subtitle">
                Manage automated notifications and communication rules
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => setError(null)}
                className="mb-4"
              >
                <FaExclamationTriangle style={{ marginRight: "0.5rem" }} />
                {error}
              </Alert>
            )}

            {/* Manual Trigger Section */}
            <Row className="mb-4">
              <Col md={8}>
                <Card
                  style={{ border: `2px solid ${CONSTANTS.COLORS.PRIMARY}` }}
                >
                  <Card.Body>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <h5
                          style={{
                            color: CONSTANTS.COLORS.PRIMARY,
                            marginBottom: "0.5rem",
                          }}
                        >
                          <FaPlay style={{ marginRight: "0.5rem" }} />
                          Manual Threshold Check
                        </h5>
                        <p className="text-muted" style={{ marginBottom: 0 }}>
                          Trigger an immediate check of all schools against
                          active rules. Scheduled runs occur daily at{" "}
                          {CONSTANTS.SCHEDULED_RUN_TIME}.
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() => handleOpenModal("confirmTrigger")}
                        disabled={triggeringJob}
                      >
                        {triggeringJob ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              style={{ marginRight: "0.5rem" }}
                            />
                            Running...
                          </>
                        ) : (
                          <>
                            <FaPlay style={{ marginRight: "0.5rem" }} />
                            Run Check Now
                          </>
                        )}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={4}>
                <Card
                  style={{
                    border: `2px solid ${CONSTANTS.COLORS.TEST}`,
                    height: "100%",
                  }}
                >
                  <Card.Body>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        height: "100%",
                      }}
                    >
                      <div>
                        <h5
                          style={{
                            color: CONSTANTS.COLORS.TEST,
                            marginBottom: "0.5rem",
                          }}
                        >
                          <FaFlask style={{ marginRight: "0.5rem" }} />
                          Test Mode
                        </h5>
                        <p className="text-muted" style={{ marginBottom: 0 }}>
                          Test rules on a single school
                        </p>
                      </div>
                      <Button
                        variant="outline-primary"
                        style={{
                          borderColor: CONSTANTS.COLORS.TEST,
                          color: CONSTANTS.COLORS.TEST,
                        }}
                        onClick={() => handleOpenModal("testSchool")}
                      >
                        <FaFlask style={{ marginRight: "0.5rem" }} />
                        Test School
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Health Warnings */}
            {/* {stats.health?.warnings && stats.health.warnings.length > 0 && (
              <Alert variant="warning" className="mb-4">
                <FaExclamationTriangle style={{ marginRight: "0.5rem" }} />
                <strong>System Warnings:</strong>
                <ul style={{ marginBottom: 0, marginTop: "0.5rem" }}>
                  {stats.health.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </Alert>
            )} */}

            {/* Dashboard Stats */}
            {loading ? (
              <div style={{ textAlign: "center", padding: "3rem" }}>
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="text-muted" style={{ marginTop: "1rem" }}>
                  Loading dashboard data...
                </p>
              </div>
            ) : (
              <div className="admin-functions-container">
                {getDashboardSections(stats, handleOpenModal).map(
                  (section, sectionIndex) => (
                    <div key={sectionIndex} className="function-category">
                      <h4 className="category-title">{section.category}</h4>
                      <div className="function-cards-grid">
                        {section.items.map((item, itemIndex) => (
                          <StatCard key={itemIndex} item={item} />
                        ))}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </Col>
        </Row>
      </Container>

      {isPortrait && <OrientationPrompt />}

      {/* Confirmation Modal */}
      <Modal
        show={showModal.confirmTrigger}
        onHide={() => handleCloseModal("confirmTrigger")}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Threshold Check</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to trigger a threshold check? This will send
          notifications to schools that meet the criteria.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => handleCloseModal("confirmTrigger")}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleTriggerJob}
            disabled={triggeringJob}
          >
            {triggeringJob ? "Running..." : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Management Modals */}
      <ManageRulesModal
        show={showModal.manageRules}
        onHide={() => handleCloseModal("manageRules")}
        onRuleUpdated={loadDashboardData}
      />

      <ManageTemplatesModal
        show={showModal.manageTemplates}
        onHide={() => handleCloseModal("manageTemplates")}
        onTemplateUpdated={loadDashboardData}
      />

      <JobHistoryModal
        show={showModal.viewJobHistory}
        onHide={() => handleCloseModal("viewJobHistory")}
      />

      <EventLogModal
        show={showModal.viewEvents}
        onHide={() => handleCloseModal("viewEvents")}
      />

      {/* Test School Modal */}
      <TestSchoolMessageModal
        show={showModal.testSchool}
        onHide={() => handleCloseModal("testSchool")}
        onTestComplete={handleTestComplete}
      />

      {/* Toast Notifications */}
      <ToastContainer position="top-end" className="p-3">
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            bg={toast.variant}
            onClose={() =>
              setToasts((prev) => prev.filter((t) => t.id !== toast.id))
            }
          >
            <Toast.Header>
              <strong className="me-auto">Notification</strong>
            </Toast.Header>
            <Toast.Body
              className={toast.variant === "danger" ? "text-white" : ""}
            >
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>
    </div>
  );
}

export default SchoolMessagingDash;